import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FilterPanel from "./components/FilterPanel";
import SeriesCard from "@/components/base/SeriesCard";
import Pagination from "@/components/base/Pagination";
import { seriesList } from "@/mocks/series";
import { categories } from "@/mocks/categories";

type SortKey = "latest" | "popular" | "score";

const PAGE_SIZE = 9;

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "按更新时间" },
  { key: "popular", label: "按热度" },
  { key: "score", label: "按评分" },
];

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type") ?? "";

  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    typeParam ? typeParam.split(",").filter(Boolean) : [],
  );
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    const fromUrl = typeParam ? typeParam.split(",").filter(Boolean) : [];
    setSelectedTypes((prev) => (prev.join(",") === fromUrl.join(",") ? prev : fromUrl));
  }, [typeParam]);

  useEffect(() => {
    document.body.style.overflow = panelOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [panelOpen]);

  const years = useMemo(
    () => Array.from(new Set(seriesList.map((item) => item.year))).sort((a, b) => b - a),
    [],
  );

  const tags = useMemo(
    () => Array.from(new Set(seriesList.flatMap((item) => item.tags))).sort(),
    [],
  );

  const filtered = useMemo(() => {
    const result = seriesList.filter((item) => {
      if (selectedTypes.length && !selectedTypes.includes(item.categoryId)) return false;
      if (selectedYears.length && !selectedYears.includes(item.year)) return false;
      if (selectedTags.length && !item.tags.some((tag) => selectedTags.includes(tag))) return false;
      return true;
    });

    return result.sort((a, b) => {
      if (sortKey === "popular") return b.popularity - a.popularity;
      if (sortKey === "score") return b.score - a.score;
      return b.releaseDate.localeCompare(a.releaseDate);
    });
  }, [selectedTypes, selectedYears, selectedTags, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const currentCategory =
    selectedTypes.length === 1
      ? categories.find((item) => item.id === selectedTypes[0])
      : undefined;

  const headingName = currentCategory ? currentCategory.name : "全部短剧";
  const headingDesc = currentCategory
    ? currentCategory.description
    : "汇集平台上所有人工智能短剧作品，支持类型、年份、标签组合筛选与多维度排序。";

  const updateTypeParam = (next: string[]) => {
    const params = new URLSearchParams(searchParams);
    if (next.length) params.set("type", next.join(","));
    else params.delete("type");
    setSearchParams(params, { replace: true });
  };

  const toggleType = (value: string) => {
    const next = selectedTypes.includes(value)
      ? selectedTypes.filter((item) => item !== value)
      : [...selectedTypes, value];
    setSelectedTypes(next);
    updateTypeParam(next);
    setPage(1);
  };

  const toggleYear = (value: number) => {
    setSelectedYears((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
    setPage(1);
  };

  const toggleTag = (value: string) => {
    setSelectedTags((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
    setPage(1);
  };

  const resetAll = () => {
    setSelectedTypes([]);
    setSelectedYears([]);
    setSelectedTags([]);
    updateTypeParam([]);
    setPage(1);
  };

  const changeSort = (key: SortKey) => {
    setSortKey(key);
    setPage(1);
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/category" className="transition-colors hover:text-primary-600">
          短剧分类
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-700">{headingName}</span>
      </nav>

      <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {headingName} - ai短剧短剧推荐
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{headingDesc}</p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="hidden lg:col-span-3 lg:block">
          <div className="sticky top-24">
            <FilterPanel
              categories={categories}
              years={years}
              tags={tags}
              selectedTypes={selectedTypes}
              selectedYears={selectedYears}
              selectedTags={selectedTags}
              onToggleType={toggleType}
              onToggleYear={toggleYear}
              onToggleTag={toggleTag}
              onReset={resetAll}
            />
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部短剧
            </p>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
              >
                <i className="ri-filter-3-line"></i>
                筛选
                {selectedTypes.length + selectedYears.length + selectedTags.length > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-semibold text-background-50">
                    {selectedTypes.length + selectedYears.length + selectedTags.length}
                  </span>
                )}
              </button>

              <div className="relative">
                <select
                  value={sortKey}
                  onChange={(event) => changeSort(event.target.value as SortKey)}
                  aria-label="排序方式"
                  className="h-10 cursor-pointer rounded-md border border-background-300 bg-background-50 pl-9 pr-8 text-sm text-foreground-800 outline-none transition-colors hover:bg-background-100 focus:border-primary-400"
                >
                  {sortOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <i className="ri-sort-desc pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
              </div>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-5">
                {pageItems.map((series) => (
                  <SeriesCard key={series.id} series={series} />
                ))}
              </div>
              <Pagination current={safePage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-movie-2-line text-3xl"></i>
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
                暂无符合筛选条件的短剧
              </h3>
              <p className="mt-2 max-w-md text-sm text-foreground-500">
                试着减少一些筛选条件，或者直接重置，浏览全部作品。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setPanelOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-foreground-950">筛选短剧</h2>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              categories={categories}
              years={years}
              tags={tags}
              selectedTypes={selectedTypes}
              selectedYears={selectedYears}
              selectedTags={selectedTags}
              onToggleType={toggleType}
              onToggleYear={toggleYear}
              onToggleTag={toggleTag}
              onReset={resetAll}
            />
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="mt-4 w-full rounded-md bg-primary-500 py-3 text-sm font-semibold text-background-50"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}